import AuthApi from '@apis/auth/AuthApi';
import {
  ACCESS_TOKEN_MAX_AGE_SECOND,
  REFRESH_TOKEN_MAX_AGE_SECOND,
} from '@constants/auth';
import {
  renderConfirmationNeededSoftWarning,
} from '@datadog/datadog-ci/dist/commands/lambda/renderers/instrument-uninstrument-renderer';
import useUser from '@hooks/auth/useUser';
import useToast from '@hooks/ui/useToast';
import useProjectId from '@hooks/useProjectId';
import { KakaoSignInError } from '@models/auth';
import { type TypeActivities, useFlow } from '@router/stackflow';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';

interface IFlow {
  activityName: keyof TypeActivities,
  activityParams: object,
}

function useSignInWithKakao(code: string) {
  const projectId = useProjectId();
  const [cookies, setCookies, removeCookies] = useCookies(['accessToken', 'refreshToken', 'authFlow'], {
    doNotParse: true,
  });
  const { getUserProfile } = useUser();
  const { replace, push } = useFlow();
  const toast = useToast();
  const { mutate } = useMutation({
    mutationFn: AuthApi.oauthSIgnIn,
    onSuccess(data) {
      const { token, refreshToken } = data;
      setCookies('accessToken', token, {
        path: '/',
        maxAge: ACCESS_TOKEN_MAX_AGE_SECOND,
      });
      setCookies('refreshToken', refreshToken, {
        path: '/',
        maxAge: REFRESH_TOKEN_MAX_AGE_SECOND,
      });
      getUserProfile();
      window.localStorage.setItem('lastLoginMethod', 'KAKAO');
      try {
        const parsed: IFlow[] = JSON.parse(cookies.authFlow);
        replace('MainActivity', {}, { animate: false });
        for (let i = 0; i < parsed.length; i += 1) {
          const flow = parsed[i];
          push(flow.activityName, flow.activityParams, { animate: i + 1 === parsed.length });
        }
        removeCookies('authFlow');
      } catch (e) {
        console.error(e);
        replace('MainActivity', {});
      }
    },
    onError(e: AxiosError<KakaoSignInError>) {
      if (e.response?.status === 401) {
        replace('MainActivity', {}, { animate: false });
        push('SignInActivity', { }, { animate: false });
        push('SignUpActivity', {
          kakaoId: e.response?.data.data.kakaoId,
          nickname: e.response?.data.data.nickname,
        });
      } else {
        console.error(e);
        toast.error('로그인에 실패했습니다. 잠시 후 다시 시도해주세요.');
        replace('MainActivity', {}, { animate: false });
        push('SignInActivity', { });
      }
    },
  });
  useEffect(() => {
    mutate({ code, projectId, signUpType: 'KAKAO' });
  }, [code, projectId]);
}

export default useSignInWithKakao;
